import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { strapiService } from '../../utils/api';
import authService from '../../utils/authService';
import AlertService from '../../utils/alertService';
import { parseDateSafe } from '../../utils/dateUtils';

import PayrollSummaryPanel from './components/PayrollSummaryPanel';
import PayrollFilterToolbar from './components/PayrollFilterToolbar';
import PayrollReceiptsList from './components/PayrollReceiptsList';
import PDFPreviewModal from './components/PDFPreviewModal';
import EnforcementBanner from '../../components/billing/EnforcementBanner';
import { Button, EmptyState, Note, initialsOf } from '../../components/app2';

const EmployeePayrollPortal = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [error, setError] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);

  // PDF Preview Modal state
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    receipt: null
  });

  // State for summary data
  const [summaryData, setSummaryData] = useState({
    currentMonthEarnings: 0,
    yearToDateEarnings: 0,
    pendingReceipts: 0,
    recentActivity: []
  });

  // Load employee data on component mount
  useEffect(() => {
    const loadEmployeeData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if employee data comes from navigation (Admin viewing employee)
        if (location?.state?.employeeId) {
          console.log('📍 Loading employee from navigation state (Admin flow)');
          console.log('🔍 DEBUG - Navigation state:', location?.state);
          console.log('🔍 DEBUG - CompanyData:', location?.state?.companyData);

          // Try multiple possible company name fields
          const companyName = location?.state?.companyData?.company_name ||
                              location?.state?.companyData?.name ||
                              location?.state?.companyName ||
                              'Empresa';

          console.log('🏢 Resolved company name:', companyName);

          setEmployeeData({
            id: location?.state?.employeeId,
            name: location?.state?.employeeName || 'Usuario',
            email: location?.state?.employeeEmail || '',
            position: location?.state?.employeePosition || 'Empleado',
            company: companyName,
            employeeId: `EMP-${location?.state?.employeeId || '000'}`,
            isDirectLogin: false // Admin viewing employee
          });
        } else {
          // Un ADMINISTRADOR aquí sin empleado elegido no tiene nada que ver: esta
          // pantalla es el autoservicio de UNA persona. Antes se caía en el primer
          // empleado de la empresa (uno al azar) y encima pedía
          // /employees/me/payroll-receipts, que para un admin responde 403 → la
          // pantalla mostraba "Error al cargar los recibos". Se manda a la vista
          // de recibos de la empresa, que es lo que sí busca.
          const rol = authService?.getCurrentUser()?.role;
          if (rol === 'administrator' || rol === 'company') {
            navigate('/app/receipts', { replace: true });
            return;
          }

          // Employee logged in directly - fetch profile to get employee ID
          console.log('👤 Loading employee from user profile (Employee flow)');

          const profileData = await authService?.getUserProfile();

          if (!profileData?.companies?.[0]?.employees?.[0]) {
            throw new Error('No se encontró información del empleado en el perfil');
          }

          const employee = profileData?.companies?.[0]?.employees?.[0];
          const company = profileData?.companies?.[0];
          const user = profileData?.user;

          console.log('🔍 DEBUG - Profile data structure:', {
            fullProfile: profileData,
            company: company,
            companyName: company?.name,
            companyKeys: company ? Object.keys(company) : []
          });

          console.log('✅ Employee data loaded from profile:', {
            employeeId: employee?.id,
            name: `${employee?.first_name} ${employee?.last_name}`,
            email: employee?.email,
            companyName: company?.name,
            companyRFC: company?.rfc
          });

          setEmployeeData({
            id: employee?.id,
            name: `${employee?.first_name || ''} ${employee?.last_name || ''}`.trim() || user?.username || 'Usuario',
            email: employee?.email || user?.email || '',
            position: employee?.position || 'Empleado',
            company: company?.name || company?.company_name || 'Empresa',
            employeeId: `EMP-${employee?.id || '000'}`,
            rfc: employee?.rfc,
            curp: employee?.curp,
            nss: employee?.nss,
            isDirectLogin: true // Employee logged in directly
          });
        }

      } catch (error) {
        console.error('❌ Error loading employee data:', error);
        setError('Error al cargar la información del empleado. Por favor, intenta nuevamente.');
        setLoading(false);
      }
    };

    loadEmployeeData();
  }, [location?.state]);

  // Fetch payroll receipts
  const fetchPayrollReceipts = useCallback(async () => {
    if (!employeeData?.id) {
      setError('No se ha seleccionado un empleado válido');
      setLoading(false);
      return;
    }

    try {
      setReceiptsLoading(true);
      setError(null);

      // Build filters
      const filters = {};
      if (selectedYear) filters.year = parseInt(selectedYear);
      if (selectedMonth) filters.month = parseInt(selectedMonth);
      filters.limit = 50; // Default limit

      console.log('Fetching receipts with filters:', filters);

      // Use different endpoint based on user type
      let response;
      if (employeeData?.isDirectLogin) {
        // Employee logged in directly - use /api/employees/me/payroll-receipts
        console.log('📱 Using employee direct login endpoint');
        response = await strapiService?.payroll?.getMyReceipts(filters);
      } else {
        // Admin viewing employee - use /api/employees/:id/payroll-receipts
        console.log('👨‍💼 Using admin viewing employee endpoint');
        response = await strapiService?.payroll?.getEmployeeReceipts(
          employeeData?.id,
          filters
        );
      }

      if (response?.data) {
        // Transform API data to component format
        const transformedReceipts = response?.data?.map(receipt => ({
          id: receipt?.id || receipt?.documentId,
          folio: receipt?.folio,
          uuid: receipt?.uuid,
          payPeriod: formatPayPeriod(receipt?.pay_period_start, receipt?.pay_period_end),
          periodStart: receipt?.pay_period_start,
          periodEnd: receipt?.pay_period_end,
          netSalary: receipt?.net_amount || 0,
          grossSalary: receipt?.total_perceptions || 0,
          totalAmount: receipt?.total_amount || 0,
          deductions: receipt?.total_deductions || 0,
          uploadDate: receipt?.issue_date,
          downloadDate: null, // API doesn't provide this field yet
          status: getReceiptStatus(receipt?.sat_status),
          currency: receipt?.currency || 'MXN',
          payrollType: receipt?.payroll_type,
          daysPaid: receipt?.days_paid,
          satStatus: receipt?.sat_status,
          employee: receipt?.employee,
          company: receipt?.company
        })) || [];

        // Agregar consecutivos a recibos del mismo mes
        const receiptsWithConsecutives = addConsecutiveNumbers(transformedReceipts);

        setReceipts(receiptsWithConsecutives);
        calculateSummaryData(receiptsWithConsecutives);
      } else {
        setReceipts([]);
        setSummaryData({
          currentMonthEarnings: 0,
          yearToDateEarnings: 0,
          pendingReceipts: 0,
          recentActivity: []
        });
      }

    } catch (error) {
      console.error('Error fetching payroll receipts:', error);
      setError('Error al cargar los recibos de nómina. Intenta nuevamente.');
      setReceipts([]);
    } finally {
      setReceiptsLoading(false);
      setLoading(false);
    }
  }, [employeeData?.id, employeeData?.isDirectLogin, selectedMonth, selectedYear]);

  // Fetch payroll receipts when employee data is available
  useEffect(() => {
    if (employeeData?.id) {
      fetchPayrollReceipts();
    }
  }, [employeeData?.id, selectedMonth, selectedYear, fetchPayrollReceipts]);

  // Helper function to format pay period
  // IMPORTANTE: Usa la fecha de TÉRMINO (endDate) del período, no la de inicio
  // Esto garantiza que el nombre del recibo corresponda al mes en que termina el período
  const formatPayPeriod = (startDate, endDate) => {
    if (!startDate || !endDate) return 'Período no especificado';

    // Usar parseDateSafe para evitar bug de timezone
    const end = parseDateSafe(endDate);

    if (!end) return 'Período no especificado';

    const monthNames = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];

    return `${monthNames[end.getMonth()]} ${end.getFullYear()}`;
  };

  // Helper function to add consecutive numbers to receipts with same month
  // Si hay múltiples recibos en el mismo mes, agrega "01", "02", etc.
  // Si solo hay un recibo en el mes, deja el nombre sin número
  const addConsecutiveNumbers = (receipts) => {
    if (!receipts || receipts.length === 0) return receipts;

    // Agrupar recibos por mes de término (YYYY-MM del periodEnd)
    const receiptsByMonth = receipts.reduce((acc, receipt) => {
      const endDate = parseDateSafe(receipt.periodEnd);
      if (!endDate) return acc;

      const monthKey = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}`;

      if (!acc[monthKey]) {
        acc[monthKey] = [];
      }
      acc[monthKey].push(receipt);
      return acc;
    }, {});

    // Ordenar recibos dentro de cada mes por fecha de término (más temprano primero)
    Object.keys(receiptsByMonth).forEach(monthKey => {
      receiptsByMonth[monthKey].sort((a, b) => {
        const dateA = parseDateSafe(a.periodEnd);
        const dateB = parseDateSafe(b.periodEnd);
        return dateA - dateB;
      });
    });

    // Asignar consecutivos a los recibos
    return receipts.map(receipt => {
      const endDate = parseDateSafe(receipt.periodEnd);
      if (!endDate) return receipt;

      const monthKey = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}`;
      const monthReceipts = receiptsByMonth[monthKey];

      // Solo agregar número si hay más de un recibo en el mes
      if (monthReceipts.length > 1) {
        const consecutivo = monthReceipts.findIndex(r => r.id === receipt.id) + 1;
        return {
          ...receipt,
          payPeriod: `${String(consecutivo).padStart(2, '0')} ${receipt.payPeriod}`
        };
      }

      // Si solo hay un recibo en el mes, dejar el nombre sin número
      return receipt;
    });
  };

  // Helper function to get receipt status
  const getReceiptStatus = (satStatus) => {
    switch (satStatus?.toLowerCase()) {
      case 'vigente':
        return 'downloaded'; // Available for download
      case 'cancelado':
        return 'viewed'; // Cancelled/viewed
      default:
        return 'pending';
    }
  };

  // Calculate summary data from receipts
  const calculateSummaryData = (receiptsData) => {
    const currentMonth = new Date()?.getMonth();
    const currentYear = new Date()?.getFullYear();

    let currentMonthEarnings = 0;
    let yearToDateEarnings = 0;
    let pendingReceipts = 0;
    const recentActivity = [];

    receiptsData?.forEach(receipt => {
      // IMPORTANTE: Usar periodEnd para determinar el mes del recibo
      // Los recibos de nómina corresponden al mes en que TERMINA el período
      const receiptDate = parseDateSafe(receipt?.periodEnd);
      if (!receiptDate) return;

      const receiptMonth = receiptDate?.getMonth();
      const receiptYear = receiptDate?.getFullYear();

      // Calculate year to date earnings using GROSS salary (total_perceptions)
      if (receiptYear === currentYear) {
        yearToDateEarnings += receipt?.grossSalary || 0;

        // Calculate current month earnings using GROSS salary (total_perceptions)
        if (receiptMonth === currentMonth) {
          currentMonthEarnings += receipt?.grossSalary || 0;
        }
      }

      // Count pending receipts
      if (receipt?.status === 'pending') {
        pendingReceipts++;
      }

      // Add to recent activity (latest 3 receipts)
      if (recentActivity?.length < 3) {
        recentActivity?.push({
          description: `Recibo ${receipt?.payPeriod} disponible`,
          date: new Date(receipt?.uploadDate)?.toLocaleDateString('es-MX', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          })
        });
      }
    });

    setSummaryData({
      currentMonthEarnings,
      yearToDateEarnings,
      pendingReceipts,
      recentActivity
    });
  };

  // Filter receipts based on search term
  const filteredReceipts = receipts?.filter(receipt => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm?.toLowerCase();
    return (
      receipt?.payPeriod?.toLowerCase()?.includes(searchLower) ||
      receipt?.folio?.toLowerCase()?.includes(searchLower) ||
      receipt?.uuid?.toLowerCase()?.includes(searchLower)
    );
  });

  // Download PDF from API
  const handleDownload = async (receipt) => {
    try {
      console.log('📥 Loading PDF for receipt:', receipt?.id);

      // Show loading state
      AlertService?.loading('Cargando PDF...', 'Por favor espere mientras se carga el recibo.');

      // Download PDF blob from API
      const pdfBlob = await strapiService?.payroll?.downloadPDF(receipt?.id);

      // Create a URL for the blob
      const pdfUrl = window.URL.createObjectURL(pdfBlob);

      // Close loading alert
      AlertService?.close();

      // Open PDF preview modal with the blob URL
      setPreviewModal({
        isOpen: true,
        receipt: {
          ...receipt,
          pdfUrl: pdfUrl // Add PDF URL to receipt data for modal
        }
      });

    } catch (error) {
      console.error('❌ Error loading PDF:', error);

      AlertService?.close();

      // Handle specific errors
      if (error?.response?.status === 404) {
        await AlertService?.error(
          'Recibo no encontrado',
          'El recibo de nómina no existe o fue eliminado.'
        );
      } else if (error?.response?.status === 403) {
        await AlertService?.error(
          'Sin permisos',
          'No tiene permisos para ver este recibo de nómina.'
        );
      } else if (error?.response?.status === 400) {
        await AlertService?.error(
          'Error al generar PDF',
          'Ocurrió un error al generar el PDF del recibo. Por favor contacte a soporte.'
        );
      } else {
        await AlertService?.error(
          'Error al cargar PDF',
          'No se pudo cargar el PDF. Por favor intente nuevamente.'
        );
      }
    }
  };

  // Handle closing PDF preview modal
  const handleClosePreview = () => {
    // Clean up blob URL to prevent memory leaks
    if (previewModal?.receipt?.pdfUrl) {
      window.URL.revokeObjectURL(previewModal?.receipt?.pdfUrl);
    }

    setPreviewModal({
      isOpen: false,
      receipt: null
    });
  };

  const handleClearFilters = () => {
    setSelectedMonth('');
    setSelectedYear('');
    setSearchTerm('');
  };

  const handleLogout = () => {
    // Clear authentication and redirect
    localStorage.removeItem('strapi_jwt_token');
    localStorage.removeItem('user_data');
    window.location.href = '/login';
  };

  // Show error state
  if (error && !employeeData?.id) {
    return (
      <EmptyState
        icon="error"
        size="lg"
        title="No se pudo cargar el portal"
        text={error}
        action={<Button variant="outline" icon="arrow_back" onClick={() => window.history?.back()}>Regresar</Button>}
      />
    );
  }

  return (
    <div className="a2-anim-screen">
      {/* Aviso de límite de plan (solo para company_admin con sobrecupo).
          La ranura colapsa sola cuando el banner no se pinta. */}
      <div className="a2-slot"><EnforcementBanner /></div>

      {/* Ficha del colaborador */}
      <div
        className="a2-card"
        style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14,
          padding: '20px 22px', marginBottom: 16,
        }}
      >
        <span className="a2-avatar" style={{ width: 56, height: 56, borderRadius: 15, fontSize: '1.3rem' }}>
          {initialsOf(employeeData?.name)}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="a2-serif" style={{ fontSize: '1.4rem', fontWeight: 600, color: 'var(--heading)' }}>
            {employeeData?.name || 'Portal del empleado'}
          </div>
          {/* Sin truncar: en móvil es preferible que la línea fluya a que se
              coma el número de empleado. */}
          <div style={{ font: "500 .88rem 'Hanken Grotesk'", color: 'var(--muted)' }}>
            {[employeeData?.position, employeeData?.company, employeeData?.employeeId]
              .filter(Boolean).join(' · ')}
          </div>
        </div>
      </div>

      {/* Error no bloqueante (el perfil cargó pero falló la consulta de recibos) */}
      {error && employeeData?.id ? (
        <Note tone="danger" className="a2-anim-fadein" style={{ marginBottom: 16 }}>
          {error}
        </Note>
      ) : null}

      <div className="a2-grid-2-wide">
        <div>
          <PayrollFilterToolbar
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            searchTerm={searchTerm}
            onMonthChange={setSelectedMonth}
            onYearChange={setSelectedYear}
            onSearchChange={setSearchTerm}
            onClearFilters={handleClearFilters}
          />

          <PayrollReceiptsList
            receipts={filteredReceipts}
            onDownload={handleDownload}
            loading={receiptsLoading}
          />
        </div>

        <PayrollSummaryPanel summaryData={summaryData} />
      </div>

      {/* Visor del recibo (PDF real) */}
      <PDFPreviewModal
        isOpen={previewModal?.isOpen}
        onClose={handleClosePreview}
        receipt={previewModal?.receipt}
      />
    </div>
  );
};

export default EmployeePayrollPortal;